export function normalizeRoleToken(value) {
  if (typeof value !== 'string') return '';
  const lower = value.trim().toLowerCase();
  if (!lower) return '';
  const collapsed = lower.replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');
  if (collapsed === 'testgpv') return 'test-gpv';
  return collapsed;
}

export function isGpvRmaRole(value) {
  return normalizeRoleToken(value) === 'gpv-rma';
}

function expandRoleSource(source) {
  if (source == null) return [];
  if (Array.isArray(source)) return source;
  if (typeof source === 'string') {
    const trimmed = source.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (_err) {
        // ignore parse errors for non-JSON strings
      }
    }
    return [trimmed];
  }
  return [];
}

// Every role token a user carries (normalized, hyphenated), across all metadata
// sources and array elements — mirrors the backend resolveRolesForRequest so the
// UI and API agree on a multi-role user.
export function collectRoleTokens(user) {
  if (!user) return [];
  const sources = [
    user?.publicMetadata?.roles, user?.publicMetadata?.role,
    user?.privateMetadata?.roles, user?.privateMetadata?.role,
    user?.unsafeMetadata?.roles, user?.unsafeMetadata?.role,
    user?.roles, user?.role,
  ];
  const out = new Set();
  for (const source of sources) {
    for (const candidate of expandRoleSource(source)) {
      const token = normalizeRoleToken(String(candidate));
      if (token) out.add(token);
    }
  }
  return Array.from(out);
}

const STAFF_ROLE_TOKENS = ['admin', 'quality-manager', 'production-engineer'];
// Supplier keys in normalized (hyphenated) token form; mapped to the canonical
// stored key (underscore) by supplierKeyForUser.
const SUPPLIER_ROLE_TOKENS = ['gpv', 'ubiqconn', 'acmepoint', 'hk-ringer'];

// NI staff = admin / quality-manager / production-engineer / service*. Matches
// backend isStaff so "any staff role wins" is consistent across the stack.
export function userIsNiStaff(user) {
  return collectRoleTokens(user).some(r => STAFF_ROLE_TOKENS.includes(r) || r.startsWith('service'));
}

// Who can edit product metadata: admin + service (matches backend canEdit).
export function userCanEditProducts(user) {
  return collectRoleTokens(user).some(r => r === 'admin' || r.startsWith('service'));
}

// Canonical supplier key (underscore form, e.g. 'hk_ringer') for a NON-staff
// supplier user, else null. Mirrors backend getSupplierKey + resolveProductAccess:
// any staff role => null (see-all); a test- prefix is stripped.
export function supplierKeyForUser(user) {
  if (userIsNiStaff(user)) return null;
  for (const r of collectRoleTokens(user)) {
    const base = r.replace(/^test-/, '');
    if (SUPPLIER_ROLE_TOKENS.includes(base)) return base.replace(/-/g, '_');
  }
  return null;
}

// May this user reach the product catalogue at all? NI staff (see everything) or a
// supplier (sees only their own products). Mirrors backend resolveProductAccess so
// the route guard and the API agree — the catalogue is NOT open to every signed-in
// user. Used to gate /products, /product/:productNumber and /product-sheet.
export function userCanViewProducts(user) {
  return userIsNiStaff(user) || !!supplierKeyForUser(user);
}

export function isTestGpv(user) {
  if (!user) return false;

  const sources = [
    user?.publicMetadata?.roles,
    user?.privateMetadata?.roles,
    user?.unsafeMetadata?.roles,
    user?.roles,
    user?.publicMetadata?.role,
    user?.privateMetadata?.role,
    user?.unsafeMetadata?.role,
    user?.role
  ];

  for (const source of sources) {
    const candidates = expandRoleSource(source);
    for (const candidate of candidates) {
      const normalized = normalizeRoleToken(String(candidate));
      if (normalized === 'test-gpv') {
        return true;
      }
    }
  }

  return false;
}
